import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Upload,
  Camera,
  Image as ImageIcon,
  Images,
  Loader2,
  CheckCircle2,
  AlertCircle,
  X,
  Plus,
  ScanBarcode,
  Layers,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import BatchConfirmationGrid from './BatchConfirmationGrid';

/**
 * MultiImageUpload - Phase 13 Component
 * 
 * Allows DRLPs to upload up to 5 shelf photos at once.
 * Extracts barcodes from all images with deduplication across images.
 */
export default function MultiImageUpload({ onComplete, onCancel }) {
  const { t } = useLanguage();
  const fileInputRef = useRef(null);
  
  // State
  const [mode, setMode] = useState('upload'); // 'upload', 'preview', 'scanning', 'results'
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanResults, setScanResults] = useState(null);
  const [imageMetadata, setImageMetadata] = useState([]);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const MAX_IMAGES = 5;
  const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

  // Handle file selection
  const handleFileSelect = useCallback((event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    
    // Check total count
    const totalCount = selectedFiles.length + files.length;
    if (totalCount > MAX_IMAGES) {
      setError(t('multi_image.max_files_error', `Maximum ${MAX_IMAGES} images allowed`));
      return;
    }
    
    // Validate each file
    const validFiles = [];
    const newPreviews = [];
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: ${t('multi_image.invalid_type', 'Must be an image file')}`);
        continue;
      }
      
      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name}: ${t('multi_image.too_large', 'Image too large (max 15MB)')}`);
        continue;
      }
      
      validFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }
    
    if (validFiles.length === 0) {
      return;
    }
    
    setError(null);
    setSelectedFiles(prev => [...prev, ...validFiles]);
    setPreviewUrls(prev => [...prev, ...newPreviews]);
    
    if (selectedFiles.length === 0 && validFiles.length > 0) {
      setMode('preview');
    }
    
    // Clear input for re-selection
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [selectedFiles, t]);

  // Remove a specific image
  const removeImage = useCallback((index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
    setPreviewUrls(prev => {
      // Revoke the URL to free memory
      URL.revokeObjectURL(prev[index]);
      return prev.filter((_, i) => i !== index);
    });
    
    // If no files left, go back to upload mode
    if (selectedFiles.length <= 1) {
      setMode('upload');
    }
  }, [selectedFiles.length]);

  // Handle drag and drop
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = Array.from(event.dataTransfer.files || []);
    if (files.length) {
      const fakeEvent = { target: { files } };
      handleFileSelect(fakeEvent);
    }
  }, [handleFileSelect]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  // Reset state
  const handleReset = useCallback(() => {
    setMode('upload');
    setSelectedFiles([]);
    // Revoke all preview URLs
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    setPreviewUrls([]);
    setScanResults(null);
    setImageMetadata([]);
    setError(null);
    setScanProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [previewUrls]);

  // Scan all images
  const handleScan = useCallback(async () => {
    if (selectedFiles.length === 0) return;
    
    setScanning(true);
    setMode('scanning');
    setError(null);
    setScanProgress(10);
    
    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });
      
      setScanProgress(20);
      
      const response = await api.post('/barcode/scan-multi-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total);
          setScanProgress(20 + percentCompleted);
        }
      });
      
      setScanProgress(85);
      
      if (response.data.success) {
        setScanResults(response.data);
        setImageMetadata(response.data.images_processed || []);
        setMode('results');
        
        const { count, ready_count, needs_input_count, duplicates_removed } = response.data;
        
        let message = t('multi_image.found_products', `Found ${count} unique products`);
        if (duplicates_removed > 0) {
          message += ` (${duplicates_removed} ${t('multi_image.duplicates_removed', 'duplicates removed')})`;
        }
        message += `: ${ready_count} ${t('batch_grid.ready', 'ready')}, ${needs_input_count} ${t('batch_grid.need_attention', 'need attention')}`;
        
        toast.success(message);
      } else {
        setError(response.data.message || t('multi_image.no_barcodes', 'No barcodes detected'));
        setMode('preview');
      }
      
      setScanProgress(100);
      
    } catch (err) {
      console.error('Multi-image scan error:', err);
      setError(err.response?.data?.detail || t('multi_image.scan_failed', 'Failed to scan images. Please try again.'));
      setMode('preview');
    } finally {
      setScanning(false);
    }
  }, [selectedFiles, t]);

  // Submit batch to create RSHDs
  const handleBatchSubmit = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/barcode/batch-create-rshd', { items });
      
      if (response.data.success) {
        const { created, failed } = response.data.summary;
        
        if (created > 0) {
          toast.success(t('multi_image.posted_success', `Successfully posted ${created} products!`));
        }
        
        if (failed > 0) {
          toast.warning(`${failed} ${t('multi_image.could_not_post', 'products could not be posted')}`);
        }
        
        if (onComplete) {
          onComplete(response.data);
        }
        
        handleReset();
      }
    } catch (err) {
      console.error('Batch submit error:', err);
      toast.error(err.response?.data?.detail || t('multi_image.post_failed', 'Failed to post products'));
    } finally {
      setSubmitting(false);
    }
  }, [onComplete, handleReset, t]);

  // Render upload zone
  const renderUploadZone = () => (
    <Card className="border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
      <CardContent className="pt-6">
        <div
          className="flex flex-col items-center justify-center py-12 cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          data-testid="multi-image-upload-zone"
        >
          <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full flex items-center justify-center mb-4">
            <Images className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('multi_image.upload_title', 'Upload Multiple Shelf Photos')}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm mb-4">
            {t('multi_image.upload_desc', 'Upload up to 5 photos of different shelves or sections. Barcodes will be extracted from all images with automatic deduplication.')}
          </p>
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <ImageIcon className="w-4 h-4" />
            <span>{t('multi_image.file_info', 'JPEG, PNG • Max 15MB each • Up to 5 images')}</span>
          </div>
          
          <Button className="mt-6" data-testid="select-images-btn">
            <Camera className="w-4 h-4 mr-2" />
            {t('multi_image.select_images', 'Select Images')}
          </Button>
        </div>
        
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          data-testid="file-input-multi"
        />
      </CardContent>
    </Card>
  );

  // Render preview grid
  const renderPreview = () => (
    <div className="space-y-4">
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" />
              {t('multi_image.selected_images', 'Selected Images')}
              <Badge variant="secondary">{selectedFiles.length}/{MAX_IMAGES}</Badge>
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={handleReset} className="text-red-500 hover:text-red-700">
              <Trash2 className="w-4 h-4 mr-1" />
              {t('scanner_ui.clear_all', 'Clear All')}
            </Button>
          </div>
          <CardDescription>
            {t('multi_image.preview_desc', 'Review your images before scanning. Barcodes from all images will be combined and deduplicated.')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Image Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
            {previewUrls.map((url, idx) => (
              <div 
                key={idx} 
                className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border group"
                data-testid={`preview-image-${idx}`}
              >
                <img
                  src={url}
                  alt={`Shelf ${idx + 1}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-1 right-1 w-6 h-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeImage(idx)}
                  data-testid={`remove-image-${idx}`}
                >
                  <X className="w-3 h-3" />
                </Button>
                <Badge className="absolute bottom-1 left-1 text-xs bg-black/50">
                  {idx + 1}
                </Badge>
              </div>
            ))}
            
            {/* Add More Button */}
            {selectedFiles.length < MAX_IMAGES && (
              <div
                className="aspect-square rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-400 
                           flex flex-col items-center justify-center cursor-pointer transition-colors"
                onClick={() => fileInputRef.current?.click()}
                data-testid="add-more-images"
              >
                <Plus className="w-6 h-6 text-gray-400 mb-1" />
                <span className="text-xs text-gray-500">{t('multi_image.add_more', 'Add More')}</span>
              </div>
            )}
          </div>
          
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset} className="flex-1">
              {t('batch_grid.cancel', 'Cancel')}
            </Button>
            <Button 
              onClick={handleScan} 
              disabled={scanning || selectedFiles.length === 0} 
              className="flex-1"
              data-testid="scan-all-btn"
            >
              <ScanBarcode className="w-4 h-4 mr-2" />
              {t('multi_image.scan_all', 'Scan All Images')} ({selectedFiles.length})
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render scanning progress
  const renderScanning = () => (
    <Card>
      <CardContent className="pt-6">
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-purple-50 rounded-full flex items-center justify-center mb-4 animate-pulse">
            <Images className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t('multi_image.scanning', 'Scanning Multiple Images...')}
          </h3>
          <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
            {t('multi_image.scanning_desc', 'Processing all images and extracting barcodes with deduplication')}
          </p>
          
          <div className="w-full max-w-xs">
            <Progress value={scanProgress} className="h-2" />
            <p className="text-xs text-gray-400 text-center mt-2">{scanProgress}%</p>
          </div>
          
          {/* Image thumbnails showing progress */}
          <div className="flex gap-2 mt-6">
            {previewUrls.slice(0, 5).map((url, idx) => (
              <div 
                key={idx} 
                className={`w-12 h-12 rounded overflow-hidden border-2 ${
                  scanProgress > (idx + 1) * (100 / selectedFiles.length) 
                    ? 'border-green-500' 
                    : 'border-gray-200'
                }`}
              >
                <img src={url} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  // Render results
  const renderResults = () => {
    if (!scanResults || !scanResults.barcodes) {
      return null;
    }
    
    return (
      <div className="space-y-4">
        {/* Summary Header */}
        <Card className="bg-gradient-to-r from-blue-50 via-purple-50 to-green-50">
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {t('multi_image.scan_complete', 'Multi-Image Scan Complete')}
                  </h3>
                  <p className="text-sm text-gray-600">
                    {selectedFiles.length} {t('multi_image.images_processed', 'images processed')} • {scanResults.count} {t('multi_image.unique_products', 'unique products found')}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  {scanResults.ready_count} {t('batch_grid.ready', 'ready')}
                </Badge>
                {scanResults.needs_input_count > 0 && (
                  <Badge variant="outline" className="bg-yellow-50 text-yellow-700">
                    <AlertCircle className="w-3 h-3 mr-1" />
                    {scanResults.needs_input_count} {t('batch_grid.need_attention', 'need attention')}
                  </Badge>
                )}
                {scanResults.duplicates_removed > 0 && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700">
                    <Layers className="w-3 h-3 mr-1" />
                    {scanResults.duplicates_removed} {t('multi_image.duplicates_removed', 'duplicates removed')}
                  </Badge>
                )}
              </div>
            </div>
            
            {/* Per-image status */}
            {imageMetadata.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2">{t('multi_image.per_image_status', 'Per-image status:')}</p>
                <div className="flex flex-wrap gap-2">
                  {imageMetadata.map((meta, idx) => (
                    <div key={idx} className="flex items-center gap-1.5">
                      <div className="w-8 h-8 rounded overflow-hidden border">
                        {previewUrls[idx] && (
                          <img src={previewUrls[idx]} alt="" className="w-full h-full object-cover" />
                        )}
                      </div>
                      {meta.status === 'success' ? (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                          {meta.barcodes_found}
                        </Badge>
                      ) : (
                        <Badge variant="destructive" className="text-xs">
                          {t('multi_image.error', 'Error')}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Batch Confirmation Grid */}
        <BatchConfirmationGrid
          items={scanResults.barcodes}
          onSubmit={handleBatchSubmit}
          onCancel={handleReset}
          loading={submitting}
          title={t('multi_image.review_products', 'Review & Post Products')}
          submitLabel={t('multi_image.post_selected', 'Post Selected')}
        />
        
        {/* Back to scan option */}
        <div className="text-center">
          <Button variant="ghost" onClick={handleReset}>
            <Images className="w-4 h-4 mr-2" />
            {t('multi_image.scan_more', 'Scan More Images')}
          </Button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4" data-testid="multi-image-upload">
      {mode === 'upload' && renderUploadZone()}
      {mode === 'preview' && renderPreview()}
      {mode === 'scanning' && renderScanning()}
      {mode === 'results' && renderResults()}
    </div>
  );
}
